import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { trpc } from "@/utils/trpc";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownRight,
  CreditCard,
  PiggyBank,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { useMemo } from "react";

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(amount);
}

interface DashboardOverviewProps {
  selectedPeriod?: {
    startDate: Date;
    endDate: Date;
    type: "current" | "month" | "custom";
  };
}

export function DashboardOverview({ selectedPeriod }: DashboardOverviewProps) {
  // Use selected period or default to current month
  const { currentDate, startOfMonth, endOfMonth } = useMemo(() => {
    if (selectedPeriod) {
      return {
        currentDate: selectedPeriod.endDate,
        startOfMonth: selectedPeriod.startDate,
        endOfMonth: selectedPeriod.endDate,
      };
    }

    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { currentDate: now, startOfMonth: start, endOfMonth: end };
  }, [selectedPeriod]);

  const stats = useQuery(
    trpc.transactions.stats.queryOptions({
      startDate: startOfMonth.toISOString(),
      endDate: endOfMonth.toISOString(),
    })
  );

  const categoryStats = useQuery(
    trpc.transactions.categoryStats.queryOptions({
      startDate: startOfMonth.toISOString(),
      endDate: endOfMonth.toISOString(),
      type: "debit",
    })
  );
  const household = useQuery(trpc.household.current.queryOptions());

  // New Sprint 2: Daily budget queries
  const dailyBudget = useQuery(trpc.budgets.getDailyBudget.queryOptions());
  const yesterdayPerformance = useQuery(
    trpc.budgets.getYesterdayPerformance.queryOptions()
  );
  const todayProgress = useQuery(trpc.budgets.getTodayProgress.queryOptions());

  // New Sprint 3: Cash flow projection
  const cashflowProjection = useQuery(
    trpc.cashflow.getCurrentMonth.queryOptions()
  );

  // New Sprint 4: Category health
  const categoryHealth = useQuery(trpc.categories.getHealth.queryOptions());

  if (stats.isPending) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="font-medium text-sm">Loading...</CardTitle>
              <div className="h-4 w-4 animate-pulse rounded bg-muted" />
            </CardHeader>
            <CardContent>
              <div className="mb-1 h-8 animate-pulse rounded bg-muted" />
              <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const data = stats.data || {
    totalIncome: 0,
    totalExpenses: 0,
    netFlow: 0,
    transactionCount: 0,
    averageTransaction: 0,
    incomeCount: 0,
    expenseCount: 0,
  };

  // Type assertion to ensure we have all the properties we need
  const typedData = data as typeof data & {
    incomeCount: number;
    expenseCount: number;
  };
  const categoryData = categoryStats.data || [];
  const householdData = household.data;

  const netIncome = typedData.totalIncome - typedData.totalExpenses;
  const isProfit = netIncome >= 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-2">
        <h2 className="font-bold text-3xl tracking-tight">
          Bem-vindo de volta, {householdData?.name || "Usuário"}!
        </h2>
        <p className="text-muted-foreground">
          Aqui está sua visão financeira para{" "}
          {currentDate.toLocaleDateString("pt-BR", {
            month: "long",
            year: "numeric",
          })}
        </p>
      </div>

      {/* Main Stats Cards - Priority Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Card 1: Quanto Posso Gastar Hoje */}
        <Card className="border-green-200 bg-green-50/50 dark:bg-green-950/20 md:col-span-2 lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-medium text-base">
              💰 Quanto Posso Gastar Hoje
            </CardTitle>
            <PiggyBank className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            {dailyBudget.data && todayProgress.data ? (
              <>
                <div className="font-bold text-4xl text-green-600">
                  {formatCurrency(todayProgress.data.remaining)}
                </div>
                <p className="text-muted-foreground text-sm mb-3">
                  Disponível para gastar hoje
                </p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Gasto hoje</span>
                    <span className="font-medium">
                      {formatCurrency(todayProgress.data.spentToday)} de{" "}
                      {formatCurrency(todayProgress.data.dailyBudget)}
                    </span>
                  </div>
                  <Progress
                    value={todayProgress.data.percentageUsed}
                    className="h-2"
                  />
                  <p className="text-muted-foreground text-xs">
                    {todayProgress.data.percentageUsed.toFixed(0)}% usado hoje
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="font-bold text-4xl text-muted-foreground">
                  {formatCurrency(0)}
                </div>
                <p className="text-muted-foreground text-sm">
                  Configure fontes de renda para calcular
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Card 2: Economia Até Ontem */}
        <Card
          className={`${
            yesterdayPerformance.data?.difference &&
            yesterdayPerformance.data.difference >= 0
              ? "border-blue-200 bg-blue-50/50 dark:bg-blue-950/20"
              : "border-orange-200 bg-orange-50/50 dark:bg-orange-950/20"
          }`}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-medium text-sm">
              💵 Economia Até Ontem
            </CardTitle>
            {yesterdayPerformance.data?.difference &&
            yesterdayPerformance.data.difference >= 0 ? (
              <TrendingUp className="h-4 w-4 text-blue-600" />
            ) : (
              <TrendingDown className="h-4 w-4 text-orange-600" />
            )}
          </CardHeader>
          <CardContent>
            {yesterdayPerformance.data ? (
              <>
                <div
                  className={`font-bold text-2xl ${
                    yesterdayPerformance.data.difference >= 0
                      ? "text-blue-600"
                      : "text-orange-600"
                  }`}
                >
                  {formatCurrency(
                    Math.abs(yesterdayPerformance.data.difference)
                  )}
                </div>
                <p className="text-muted-foreground text-xs">
                  {yesterdayPerformance.data.difference >= 0
                    ? "✅ Economizado ontem"
                    : "⚠️ Acima do orçamento"}
                </p>
              </>
            ) : (
              <>
                <div className="font-bold text-2xl text-muted-foreground">
                  {formatCurrency(0)}
                </div>
                <p className="text-muted-foreground text-xs">
                  Aguardando dados de orçamento
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Card 3: Gastos Fixos */}
        <Card className="border-red-200 bg-red-50/50 dark:bg-red-950/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-medium text-sm">
              📌 Gastos Fixos
            </CardTitle>
            <CreditCard className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            {dailyBudget.data ? (
              <>
                <div className="font-bold text-2xl text-red-600">
                  {formatCurrency(dailyBudget.data.fixedExpenses)}
                </div>
                <p className="text-muted-foreground text-xs">
                  {dailyBudget.data.totalIncome > 0
                    ? `${((dailyBudget.data.fixedExpenses / dailyBudget.data.totalIncome) * 100).toFixed(0)}% da renda`
                    : "Gastos fixos mensais"}
                </p>
              </>
            ) : (
              <>
                <div className="font-bold text-2xl text-muted-foreground">
                  {formatCurrency(0)}
                </div>
                <p className="text-muted-foreground text-xs">
                  Configure gastos fixos recorrentes
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Card 4: Gastos Variáveis Este Mês */}
        <Card className="border-purple-200 bg-purple-50/50 dark:bg-purple-950/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-medium text-sm">
              🛒 Gastos Variáveis
            </CardTitle>
            <ArrowDownRight className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            {dailyBudget.data ? (
              <>
                <div className="font-bold text-2xl text-purple-600">
                  {formatCurrency(
                    Math.max(
                      0,
                      typedData.totalExpenses -
                        dailyBudget.data.fixedExpenses -
                        dailyBudget.data.blockedCategoriesAmount
                    )
                  )}
                </div>
                <p className="text-muted-foreground text-xs">
                  {typedData.expenseCount} transações este mês
                </p>
              </>
            ) : (
              <>
                <div className="font-bold text-2xl text-purple-600">
                  {formatCurrency(typedData.totalExpenses)}
                </div>
                <p className="text-muted-foreground text-xs">
                  {typedData.expenseCount} transações este mês
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cash Flow Projection Card - Sprint 3 */}
      {cashflowProjection.data && (
        <Card className="border-purple-200 bg-purple-50/50 dark:bg-purple-950/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-medium text-lg">
              📊 Saldo Projetado
            </CardTitle>
            <TrendingUp className="h-5 w-5 text-purple-600" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div
                className={`font-bold text-4xl ${
                  cashflowProjection.data.endingBalance < 0
                    ? "text-red-600"
                    : cashflowProjection.data.endingBalance < 1000
                      ? "text-orange-600"
                      : "text-purple-600"
                }`}
              >
                {formatCurrency(cashflowProjection.data.endingBalance)}
              </div>
              <p className="text-muted-foreground text-sm">
                Saldo previsto para fim do mês
              </p>
            </div>

            {/* Critical Days Warning */}
            {cashflowProjection.data.criticalDays > 0 ? (
              <div className="rounded-md bg-red-100 p-3 dark:bg-red-950/30">
                <p className="font-medium text-red-900 text-sm dark:text-red-200">
                  ⚠️ Atenção: {cashflowProjection.data.criticalDays}{" "}
                  {cashflowProjection.data.criticalDays === 1 ? "dia" : "dias"}{" "}
                  com saldo negativo
                </p>
              </div>
            ) : (
              <div className="rounded-md bg-green-100 p-3 dark:bg-green-950/30">
                <p className="font-medium text-green-900 text-sm dark:text-green-200">
                  ✅ Saldo positivo durante todo o mês
                </p>
              </div>
            )}

            {/* Lowest Balance Point */}
            <div className="space-y-1 border-t pt-3 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Menor saldo:</span>
                <span
                  className={`font-medium ${
                    cashflowProjection.data.lowestBalance < 0
                      ? "text-red-600"
                      : cashflowProjection.data.lowestBalance < 1000
                        ? "text-orange-600"
                        : ""
                  }`}
                >
                  {formatCurrency(cashflowProjection.data.lowestBalance)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total entradas:</span>
                <span className="font-medium text-green-600">
                  +{formatCurrency(cashflowProjection.data.totalIncome)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total saídas:</span>
                <span className="font-medium text-red-600">
                  -
                  {formatCurrency(
                    cashflowProjection.data.totalExpenses +
                      cashflowProjection.data.totalEstimatedSpending
                  )}
                </span>
              </div>
            </div>

            {/* Link to cash flow page */}
            <div className="border-t pt-3">
              <a
                href="/cashflow"
                className="text-purple-600 text-sm hover:underline"
              >
                Ver projeção detalhada dia a dia →
              </a>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category Health Card - Sprint 4 */}
      {categoryHealth.data && categoryHealth.data.categories.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-medium text-lg">
              🎯 Saúde das Categorias
            </CardTitle>
            <PiggyBank className="h-5 w-5 text-amber-600" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center gap-2">
                <div
                  className={`font-bold text-4xl ${
                    categoryHealth.data.categoriesWithIssues > 0
                      ? "text-red-600"
                      : "text-green-600"
                  }`}
                >
                  {categoryHealth.data.categoriesOk}
                </div>
                <span className="text-muted-foreground">
                  / {categoryHealth.data.categories.length}
                </span>
              </div>
              <p className="text-muted-foreground text-sm">
                Categorias dentro dos limites
              </p>
            </div>

            {/* Status Summary */}
            {categoryHealth.data.categoriesWithIssues > 0 ? (
              <div className="rounded-md bg-red-100 p-3 dark:bg-red-950/30">
                <p className="font-medium text-red-900 text-sm dark:text-red-200">
                  ⚠️ {categoryHealth.data.categoriesWithIssues}{" "}
                  {categoryHealth.data.categoriesWithIssues === 1
                    ? "categoria"
                    : "categorias"}{" "}
                  com problemas
                </p>
              </div>
            ) : (
              <div className="rounded-md bg-green-100 p-3 dark:bg-green-950/30">
                <p className="font-medium text-green-900 text-sm dark:text-green-200">
                  ✅ Todas as categorias estão saudáveis
                </p>
              </div>
            )}

            {/* Top Categories with Issues */}
            <div className="space-y-2">
              {categoryHealth.data.categories
                .filter(
                  (c) =>
                    c.status === "exceeded" ||
                    c.status === "near_max" ||
                    c.status === "below_min"
                )
                .slice(0, 3)
                .map((cat) => {
                  const percentage = cat.percentageCurrent;
                  const target = cat.percentageMax || cat.percentageMin || 0;
                  const barPercentage = Math.min(
                    (percentage / target) * 100,
                    100
                  );

                  return (
                    <div key={cat.categoryId} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1">
                          <span>{cat.categoryIcon}</span>
                          <span className="font-medium">
                            {cat.categoryName}
                          </span>
                        </span>
                        <span
                          className={
                            cat.status === "exceeded"
                              ? "text-red-600"
                              : cat.status === "near_max"
                                ? "text-orange-600"
                                : "text-blue-600"
                          }
                        >
                          {percentage.toFixed(1)}%
                        </span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-gray-200">
                        <div
                          className={`h-2 rounded-full ${
                            cat.status === "exceeded"
                              ? "bg-red-500"
                              : cat.status === "near_max"
                                ? "bg-orange-500"
                                : "bg-blue-500"
                          }`}
                          style={{ width: `${barPercentage}%` }}
                        />
                      </div>
                      <p className="text-muted-foreground text-xs">
                        {cat.message}
                      </p>
                    </div>
                  );
                })}
            </div>

            {/* Link to analysis page */}
            <div className="border-t pt-3">
              <a
                href="/categories/analysis"
                className="text-amber-600 text-sm hover:underline"
              >
                Ver análise completa de categorias →
              </a>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category Breakdown */}
      <div className="w-full">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PiggyBank className="h-5 w-5" />
              Detalhamento por Categoria
            </CardTitle>
            <CardDescription>
              Lista completa de gastos organizados por categoria -{" "}
              {currentDate.toLocaleDateString("pt-BR", {
                month: "long",
                year: "numeric",
              })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {categoryData.length === 0 ? (
              <div className="py-4 text-center">
                <p className="mb-3 text-muted-foreground text-sm">
                  Nenhuma transação encontrada para este período.
                </p>
                <p className="text-muted-foreground text-xs">
                  Adicione algumas transações para ver o detalhamento por
                  categoria.
                </p>
              </div>
            ) : (
              categoryData.map((item, index) => {
                const colors = [
                  "#FF6B6B",
                  "#4ECDC4",
                  "#95E77E",
                  "#FFE66D",
                  "#6C5CE7",
                  "#00B894",
                  "#FDCB6E",
                  "#FD79A8",
                  "#A29BFE",
                  "#B2BEC3",
                ];
                return (
                  <div className="space-y-2" key={item.categoryName}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div
                          className="h-3 w-3 rounded-full"
                          style={{
                            backgroundColor: colors[index % colors.length],
                          }}
                        />
                        <span className="font-medium">{item.categoryName}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-sm">
                          {formatCurrency(Math.abs(Number(item.total)))}
                        </span>
                        <Badge variant="outline">{item.percentage}%</Badge>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-muted-foreground text-xs">
                      <span>{item.count} transações</span>
                      <span>
                        Média:{" "}
                        {formatCurrency(Math.abs(Number(item.avgAmount)))}
                      </span>
                    </div>
                    <Progress
                      className="h-1"
                      style={{
                        background: `linear-gradient(to right, ${colors[index % colors.length]}20 0%, ${colors[index % colors.length]} ${item.percentage}%, #f1f5f9 ${item.percentage}%)`,
                      }}
                      value={item.percentage}
                    />
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
